import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MuiButton from '@mui/material/Button';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';
import { toast } from '../../../../ui/toast';
import AsyncCustomSelect from '../../../../ui/custom_react_select/async_select';
import { CustomSelect } from '../../../../ui/custom_react_select/custom_select';
import { getEmployeeList } from '../../../../utils/api_service/employees';
import { debounce } from '../../../../utils/helpers/debounce';
import {
  EmployeeUser,
  JobTitle,
  SingleEmployee,
  UserGroup,
  UserRole,
} from '../../../../utils/types/admin_types';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { OrganizationUnit } from '../../OrganizationSettings/TreeStructure';

type EditEmployeeModalProps = {
  employeeUsers: EmployeeUser[];
  organizationUnits: OrganizationUnit[];
  jobTitles: JobTitle[];
  userGroupList: UserGroup[];
  userRoleList: UserRole[];
  singleEmployee: SingleEmployee | null;
  employee: EmployeeUser;
  addNewJobTitle: (title: string) => void;
  editUser: (
    employee: EmployeeUser,
    groups: SelectOptions[],
    roles: SelectOptions[],
    selectedJobTitle: string,
    organizationUnit: string,
    reportingManager: string | undefined
  ) => void;
};

export const EditEmployeeModal: React.FC<EditEmployeeModalProps> = ({
  employeeUsers,
  organizationUnits,
  jobTitles,
  userGroupList,
  userRoleList,
  singleEmployee,
  employee,
  addNewJobTitle,
  editUser,
}: EditEmployeeModalProps) => {
  const { t } = useTranslation(NS.adminPages);
  const EM = ADMIN_PAGES_KEYS.employeeManagement;
  const [selectedGroups, setSelectedGroups] = useState(() =>
    employee?.groups.map((group) => ({
      value: group.id,
      label: group.groupName,
    })),
  );
  const [selectedRoles, setSelectedRoles] = useState(() =>
    employee?.roles.map((role) => ({
      value: role.id,
      label: role.roleName,
    })),
  );
  const [reportingManager, setReportingManager] = useState<
    SelectOptions | undefined
  >(() => {
    if (singleEmployee && singleEmployee.reportingManager) {
      const manager = singleEmployee.reportingManager;
      return {
        value: manager.id,
        label: manager.user.firstName + ' ' + manager.user.lastName,
      };
    }
  });
  const [organizationUnit, setOrganizationUnit] = useState<
    SelectOptions | undefined
  >(() => {
    if (singleEmployee && singleEmployee.organizationUnitId) {
      const orgUnit = singleEmployee.organizationUnitId;
      return { value: orgUnit.id, label: orgUnit.unitName };
    }
  });
  const [selectedJobTitle, setSelectedJobTitle] = useState<
    SelectOptions | undefined
  >(() => {
    if (employee.jobTitle && jobTitles.length > 0) {
      const title = jobTitles.find(
        (title) => title.id === employee.jobTitle.id,
      );
      if (title) {
        return { value: title.id, label: title.jobTitle };
      }
    }
  });

  const defaultManagerOptions = employeeUsers.map((employee) => ({
    value: employee.id,
    label: employee.user.firstName + ' ' + employee.user.lastName,
  }));

  const managerLoadOptions = async (query: string) => {
    try {
      const response = await getEmployeeList(query, 1, 10);
      if (response.ok) {
        const data = await response.json();

        return data.employee.map((employee: EmployeeUser) => ({
          value: employee.id,
          label: `${employee.user.firstName} ${employee.user.lastName}`,
        }));
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error fetching employees: ' + error, {
        position: 'bottom-center',
      });
      return [];
    }
  };

  const loadOptionsDebounced = debounce(
    (inputValue: string, callback: (options: any) => void) => {
      managerLoadOptions(inputValue).then((options) => callback(options));
    },
    500,
  );

  const groupOptions = useMemo(
    () =>
      userGroupList.map((group) => ({
        value: group.id,
        label: group.groupName,
      })),
    [userGroupList],
  );

  const roleOptions = useMemo(
    () =>
      userRoleList.map((role) => ({
        value: role.id,
        label: role.roleName,
      })),
    [userRoleList],
  );

  const organizationUnitOptions = useMemo(
    () =>
      organizationUnits.map((unit) => ({
        value: unit.id,
        label: unit.unitName,
      })),
    [organizationUnits],
  );

  const jobTitleOptions = useMemo(
    () =>
      jobTitles.map((title) => ({
        value: title.id,
        label: title.jobTitle,
      })),
    [jobTitles],
  );

  const handleGroupChange = (selectedOptions: SelectOptions[]) => {
    setSelectedGroups(selectedOptions);
  };

  const handleRoleChange = (selectedOptions: SelectOptions[]) => {
    setSelectedRoles(selectedOptions);
  };

  const handleManagerChange = (selectedOptions: SelectOptions) => {
    setReportingManager(selectedOptions);
  };

  const handleUnitChange = (selectedOptions: SelectOptions) => {
    setOrganizationUnit(selectedOptions);
  };

  const handleTitleChange = (selectedOptions: SelectOptions) => {
    setSelectedJobTitle(selectedOptions);
  };

  const isSubmitDisabled = !(organizationUnit !== undefined && selectedJobTitle !== undefined);

  return (
    <Stack spacing={3} sx={{ width: '100%', pt: 1 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: { xs: '100%', sm: '85%' } }}>
        <CustomSelect
          name='jobTitle'
          options={jobTitleOptions}
          onChange={handleTitleChange}
          label={t(EM.editModal.jobTitle)}
          value={selectedJobTitle}
          creatable
          onCreateOption={addNewJobTitle}
        />
        <AsyncCustomSelect
          value={reportingManager}
          defaultOptions={defaultManagerOptions}
          onChange={handleManagerChange}
          loadOptions={(inputValue, callback) =>
            loadOptionsDebounced(inputValue, callback)
          }
          label={t(EM.editModal.reportingManager)}
        />
        <CustomSelect
          name='organizationUnit'
          options={organizationUnitOptions}
          onChange={handleUnitChange}
          label={t(EM.editModal.organizationUnit)}
          value={organizationUnit}
        />
        <CustomSelect
          name='groups'
          options={groupOptions}
          onChange={handleGroupChange}
          isMulti={true}
          label={t(EM.editModal.groupMembership)}
          value={selectedGroups}
        />
        <CustomSelect
          name='roles'
          options={roleOptions}
          onChange={handleRoleChange}
          isMulti={true}
          label={t(EM.editModal.roleMembership)}
          value={selectedRoles}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <MuiButton
          onClick={() =>
            organizationUnit &&
            selectedJobTitle &&
            editUser(
              employee,
              selectedGroups,
              selectedRoles,
              selectedJobTitle.value,
              organizationUnit.value,
              reportingManager !== null && reportingManager !== undefined
                ? reportingManager.value
                : undefined,
            )
          }
          variant='contained'
          disabled={isSubmitDisabled}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: '#093725',
            '&:hover': { bgcolor: '#062619' },
          }}
        >
          {t(EM.editModal.editEmployee)}
        </MuiButton>
      </Box>
    </Stack>
  );
};
